import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EntityAttachment,
  CreateEntityAttachmentInput,
  UpdateEntityAttachmentInput,
  BulkMoveEntityAttachmentsInput,
} from '@peles/shared';
import * as api from '../api/entity-attachments';

export function useEntityAttachments(entityType: string, entityId: string) {
  return useQuery<EntityAttachment[]>({
    queryKey: ['entity-attachments', entityType, entityId],
    queryFn: () => api.listEntityAttachments(entityType, entityId),
    enabled: !!entityType && !!entityId,
  });
}

export function useAttachmentsByFolder(folderId: string | null) {
  return useQuery<EntityAttachment[]>({
    queryKey: ['entity-attachments-folder', folderId],
    queryFn: () => api.listEntityAttachmentsByFolder(folderId!),
    enabled: !!folderId,
  });
}

export function useCreateEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntityAttachmentInput) => api.createEntityAttachment(input),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({
        queryKey: ['entity-attachments', input.entityType, input.entityId],
      });
      queryClient.invalidateQueries({ queryKey: ['entity-attachments-folder'] });
    },
  });
}

export function useUpdateEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEntityAttachmentInput }) =>
      api.updateEntityAttachment(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-attachments'] });
      queryClient.invalidateQueries({ queryKey: ['entity-attachments-folder'] });
    },
  });
}

export function useBulkMoveEntityAttachments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkMoveEntityAttachmentsInput) => api.bulkMoveEntityAttachments(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-attachments-folder'] });
    },
  });
}

export function useDeleteEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; entityType: string; entityId: string }) =>
      api.deleteEntityAttachment(id),
    onSuccess: (_, { entityType, entityId }) => {
      queryClient.invalidateQueries({ queryKey: ['entity-attachments', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['entity-attachments-folder'] });
    },
  });
}
